<!--
  Component:   Field Calculation Modal: Row Formula.
  Path:        Field - Calculation - Row Formula.
  Calculation:
  - Formula       Javascript phrase, invoked by the data population engine to populate the Field. [Textbox]
  Helpers:
  - Fields        List of Fields in Table that can be embedded in the Formula. [Listbox]
  - Functions     List of Functions that can be embedded in the Formula. [Listbox]
-->
<template>
  <div>
    <b-form>
      <div class="row">

        <div class="col-4">

          <!-- Fields selector -->
          <b-form-group class="properties_group" label="Fields">
            <field-selector ref="fieldSelector" :table="table" :fields-to-ignore="selectorFieldsToIgnore"
              @add-field="addFieldToFormula" />
          </b-form-group>

          <!-- Function selection. -->
          <b-form-group v-if="functionsLoaded" class="properties_group mt-3 mb-1" label="Functions">
            <div class="search__wrapper">
              <b-input type="search" v-model="functionSearchValue" placeholder="Search" debounce="300" />
            </div>
            <b-list-group class="group__item">
              <b-list-group-item v-for="functionItem in functionsToRender" :key="functionItem.name"
                class="list__item-row-formula" :active="selectedFunctionName === functionItem.name"
                @click="setSelectedFunction(functionItem)" @dblclick="onAddFunction(functionItem)">
                <div><strong>{{ functionItem.name }}</strong> {{ getFunctionParamsForDisplay(functionItem) }}</div>
                <div>{{ functionItem.purpose }}</div>
              </b-list-group-item>
            </b-list-group>
            <b-button id="addFunctionButton" size="sm" :disabled="isAddFunctionButtonDisabled" block
              @click="onAddFunction()">
              <font-awesome-icon :icon="['fas', 'plus']" />
              Add Function
            </b-button>
          </b-form-group>

        </div>

        <div class="col-8">

          <!-- Formula Editor-->
          <calculation-formula-code-editor id="formulaEditor" ref="formulaEditor" label="Row Formula"
            language="javascript" :formula="editorFormula" :constructFieldDefinition="constructFieldDefinition" @formula-changed="onFormulaChanged"
            :calculation-warning-on-verify="calculationWarningOnVerify" :atlas-assistant="atlasAssistant" />

          <!-- Atlas Assistant -->
          <atlas-assistant ref="atlasAssistant" class="atlasAssistant mt-3 mb-1" query-type="RowFormula"
            :target-field="tableField" :query-fields="assistantQueryFields"
            :additionalFunctions="assistantAdditionalFunctions" @use-assistant-formula="useAssistantFormula"
            :log-assistant-conversation="false" />

        </div>
      </div>

    </b-form>
  </div>
</template>

<script>

import FieldSelector from '../../components/FieldSelector.vue';
import CalculationFormulaCodeEditor from './CalculationFormulaCodeEditor.vue';
import AtlasAssistant from '../../components/AtlasAssistant.vue';

export default {

  name: 'RowFormulaPanel',

  components: {
    FieldSelector,
    CalculationFormulaCodeEditor,
    AtlasAssistant,
  },

  inject: [
    /* Current Table. [from CalculationModel] */
    'table',
    /* Current Field. [from CalculationModel] */
    'tableField',
    /* Calculation data - to be populated with Lookup Pairs. [from CalculationModel] */
    'calculationData',
    /** Clone field with current Calculation properties. [from CalculationModel] */
    'constructFieldDefinition',
    /* Utility functions etc. [from CalculationModel] */
    'apiUtils',
    'aiUtils',
    'entityUtils',
  ],

  props: [],

  data() {
    return {
      /** Formula displayed in Editor (field names). */
      editorFormula: '',
      /** List of function definitions. */
      functions: [],
      functionsLoaded: false,
      /** Partial Function name to filter list. */
      functionSearchValue: '',
      /** Function selected in list. */
      selectedFunction: null,
    };
  },

  watch: {
  },

  computed: {

    fieldDataType() {
      return this.entityUtils.getFieldDataType(this.tableField)
    },

    /** Fields to exclude from selector. */
    selectorFieldsToIgnore() {
      return [this.tableField];
    },

    /** Functions to insert in Formula. */
    functionsToRender() {
      let values = [];
      if (this.functionSearchValue.length) {
        values = this.functions.filter(fn => fn.name.toLowerCase().includes(this.functionSearchValue.toLowerCase()));
      }
      else {
        values = this.functions;
      }
      return values.sort(fn => fn.name);
    },
    selectedFunctionName() {
      return this.selectedFunction?.name || '';
    },
    isAddFunctionButtonDisabled() {
      return !this.selectedFunctionName;
    },

    /** Fields available to be queried by the function generated in the Atlas Assistant. */
    assistantQueryFields() {
      return this.table.fields.filter(fld => fld.fieldGuid != this.tableField.fieldGuid);
    },

    /** Additional (bespoke, non-standard) functions available for the Assistant to use in the formula. */
    assistantAdditionalFunctions() {
      return this.functionsToRender
        .filter(fn => fn.bespoke && (fn.variants || []).length)
        .map(fn => `${fn.name}(${(fn.variants[0].params || []).map(prm => prm.name).join(', ')}) [${fn.purpose}].`);
    },

  },

  methods: {

    // Formula Editor utilities.
    getformulaEditor() {
      return this.$refs.formulaEditor;
    },
    onFormulaChanged(formula) {
      this.editorFormula = formula;
      this.calculationData.formula = this.entityUtils.changeFieldNamesToGuids(formula, this.table.fields);
    },
    replaceEditorTextSelection(text, cursorOffset = 0) {
      this.getformulaEditor().replaceTextSelection(text, cursorOffset);
    },
    clearformulaVerify() {
      return this.getformulaEditor().setFormulaVerify();
    },

    /** 
     * Basic validation on the Calculation to determine whether the formula can be verified.
     * - Returns a warning message to display below the formula editor in place of invoking the Verify logic.
     * - Called by Code Editor prior to verifying.
     */
    calculationWarningOnVerify(){

    },

    // Field selection.
    addFieldToFormula(field) {
      if (!field) return;
      this.replaceEditorTextSelection(`{{'${field.displayName}'}}`);
    },

    // Function selection.
    async loadFunctions() {
      if (this.functionsLoaded) return;
      try {
        const response = await this.apiUtils.getFieldCalculationFunctions('javascript', this.fieldDataType);
        this.functions = response.body?.functions || [];
        this.functionsLoaded = true;
      } catch (error) {
        console.error('Error loading functions:', error);
      }
    },
    getFunctionParamsForDisplay(functionItem) {
      if ((functionItem.variants || []).length)
        return `(${(functionItem.variants[0].params || []).map(fn => fn.name).join(', ')})`;
      return '()';
    },
    setSelectedFunction(functionItem) {
      this.selectedFunction = (functionItem.name === this.selectedFunctionName ? null : functionItem);
    },
    onAddFunction(functionItem) {

      // Check that Function is specified/selected.
      if (functionItem) this.selectedFunction = functionItem;
      if (!this.selectedFunction) return;

      // Include brackets with function name and set cursor between.
      this.replaceEditorTextSelection(`${this.selectedFunctionName}()`, -1);
      this.selectedFunction = null;
    },

    // Atlas Assistant.
    atlasAssistant() {
      return this.$refs.atlasAssistant;
    },
    useAssistantFormula(formula) {
      this.editorFormula = this.entityUtils.changeFieldGuidsToNames(formula, this.table.fields);
    },

  },

  /** Component's Reactive State is initialised but DOM not yet rendered. */
  beforeMount() {

    /* Ensure that Calculation Data is initialised. */
    if (!this.calculationData.hasOwnProperty('formula')) {
      this.$set(this.calculationData, 'formula', '');
    }

    /* Don't wait for the responses - Controls are only rendered once the data is returned and validated. */
    this.loadFunctions();

    /* Ensure that Calculation data is initialised. */
    if (!this.calculationData.hasOwnProperty('formula'))
      this.$set(this.calculationData, formula, '');

    // Populate formula to edit with field names.
    this.editorFormula = this.entityUtils.changeFieldGuidsToNames(this.calculationData.formula, this.table.fields);

  },

  /** Component's Reactive State is initialised and DOM has been rendered. */
  async mounted() {
    // Example Assistant response.
    const populateExampleAssistantData = false;
    if (populateExampleAssistantData) {
      this.atlasAssistant().aa.response.formula = `// Calculate the average of two fields, round it, and convert to string\nreturn String(Math.round(({{'DecimalField 2.1.1.1'}} + {{'FloatField 2.1.1.1'}}) / 2));\n/* Comment line 1 */\n/* Comment line 2 */\n/* Comment line 3 */\n/* Comment line 4 */\n/* Comment line 5 */\n/* Comment line 6 */\n/* Comment line 7 */\n/* Comment line 8 */\n/* Comment line 9 */\n/* Comment line 10 */`;
      this.atlasAssistant().aa.response.info = `Explanation:
- fieldA and fieldB are variables or field references.
- (fieldA + fieldB) / 2 computes the average.
- Math.round(...) rounds the result to the nearest integer (with some extra text to ensure that the comment is longer than the space available to display the info).
- String(...) converts the number to a string.`;
      //this.atlasAssistant().aa.response.error = `Not a valid response.`;
      //this.atlasAssistant().aa.response.warning = `Response may not be accurate. Fairly lengthy warning message to overflow the available space and see how wrapping works.`;
    }
  },
};

</script>

<style scoped lang="scss">
.properties_group {

  &#formulaEditor {
    ::v-deep #codeEditor {
      height: 260px;
      max-height: 320px;
    }
  }

  .atlasAssistant {
    height: 260px;
    max-height: 320px;
  }

  :deep(& > div) {

    .list-group {
      padding: 2px;
      border: 1px solid #ccc;
      border-top-right-radius: 6px;
      border-top-left-radius: 6px;
      height: 200px;
      overflow-y: auto;
    }
  }

}

label {
  margin-bottom: 0.5rem;
}

.list__item-row-formula {
  padding: 2px 4px;

  &:not(.active):hover {
    background-color: #ffffe0;
  }
}
</style>